import { Loading, Button, ButtonTypes, Checkbox, TextBox } from "@repo/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, LayoutDashboard, Plus, RefreshCw, Settings2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { MyViewController as Api, type MyViewWidget } from "../../api/myViewController";
import { BoardCanvas } from "./boards/boardCanvas";
import { keys } from "./queryKeys";
import { ExportSettings } from "./settings/exportSettings";
import { WidgetSettings } from "./settings/widgetSettings";
import { Dialog } from "./shared/dialog";
import commonStyles from "./shared/style.module.scss";
import styles from "./style.module.scss";

export default function MyView() {
	const client = useQueryClient();
	const boards = useQuery({ queryKey: keys.boards, queryFn: Api.getBoards, staleTime: 60_000 });
	const catalog = useQuery({ queryKey: keys.catalog, queryFn: Api.getCatalog, staleTime: 300_000 });
	const [selected, setSelected] = useState<number>();
	const board = boards.data?.find((b) => b.id === selected) ?? boards.data?.[0];
	const widgets = useQuery({
		queryKey: keys.widgets(board?.id ?? 0),
		queryFn: ({ signal }) => Api.getWidgets(board!.id, signal),
		enabled: !!board,
		staleTime: 60_000,
		refetchOnWindowFocus: false,
	});
	const [exporting, setExporting] = useState(false);
	const [editing, setEditing] = useState(false);
	const [refresh, setRefresh] = useState(0);
	const [live, setLive] = useState(true);
	const [cycle, setCycle] = useState(false);
	const [dialog, setDialog] = useState<
		"newBoard" | "editBoard" | "deleteBoard" | "addWidget" | "editWidget" | "deleteWidget"
	>();
	const [activeWidget, setActiveWidget] = useState<MyViewWidget>();
	const [boardTitle, setBoardTitle] = useState("");
	const [includeInCycle, setIncludeInCycle] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	useEffect(() => {
		if (!cycle || editing || dialog || exporting) return;
		const timer = window.setInterval(() => {
			if (document.hidden) return;
			const eligible = boards.data?.filter((b) => b.includeInCycle) ?? [];
			if (eligible.length > 1)
				setSelected(eligible[(eligible.findIndex((b) => b.id === board?.id) + 1) % eligible.length].id);
		}, 30_000);
		return () => clearInterval(timer);
	}, [cycle, editing, dialog, exporting, boards.data, board?.id]);
	function openBoard(edit: boolean) {
		setBoardTitle(edit ? (board?.title ?? "") : "");
		setIncludeInCycle(edit ? (board?.includeInCycle ?? true) : true);
		setError("");
		setDialog(edit ? "editBoard" : "newBoard");
	}
	async function boardAction(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError("");
		try {
			if (dialog === "deleteBoard" && board) {
				await Api.deleteBoard(board.id);
				setSelected(undefined);
			} else if (dialog === "deleteWidget" && activeWidget) {
				await Api.deleteWidget(activeWidget.id);
				await client.invalidateQueries({ queryKey: keys.widgets(board!.id) });
			} else {
				const saved = await Api.saveBoard({
					id: dialog === "editBoard" ? board!.id : 0,
					title: boardTitle,
					includeInCycle,
					sortIndex:
						dialog === "editBoard"
							? board!.sortIndex
							: Math.max(-1, ...(boards.data ?? []).map((b) => b.sortIndex)) + 1,
				});
				setSelected(saved.id);
			}
			await client.invalidateQueries({ queryKey: keys.boards });
			setDialog(undefined);
		} catch {
			setError("Your changes could not be saved. Please try again.");
		} finally {
			setBusy(false);
		}
	}
	return (
		<main className={styles.page}>
			{exporting && board && (
				<ExportSettings
					key={board.id}
					id={board.id}
					board
					title={board.title}
					onClose={() => setExporting(false)}
				/>
			)}
			<header className={styles.pageHeader}>
				<div className={styles.title}>
					<div className={styles.logo}>
						<LayoutDashboard size={24} />
					</div>
					<div>
						<h1>Viewboard</h1>
					</div>
				</div>
				<div className={styles.headerActions}>
					<Button disabled={editing || !board} onClick={() => setExporting(true)}>
						Automated export
					</Button>
					<Button
						disabled={editing || !board}
						onClick={() => {
							setRefresh((r) => r + 1);
							void widgets.refetch();
						}}
					>
						<RefreshCw size={16} />
						Refresh
					</Button>
					<Button
						types={[ButtonTypes.Primary]}
						disabled={editing || !board || !catalog.data || widgets.isPending || widgets.isError}
						onClick={() => {
							setActiveWidget(undefined);
							setDialog("addWidget");
						}}
					>
						<Plus size={17} />
						Add widget
					</Button>
				</div>
			</header>
			<nav className={styles.boardBar} aria-label="Your boards">
				<div className={styles.boardTabs}>
					{boards.data?.map((b) => (
						<Button
							key={b.id}
							types={[b.id === board?.id ? ButtonTypes.Primary : ButtonTypes.Text]}
							aria-current={b.id === board?.id ? "page" : undefined}
							disabled={editing}
							onClick={() => setSelected(b.id)}
						>
							{b.title}
						</Button>
					))}
					<Button
						types={[ButtonTypes.Icon]}
						aria-label="Create board"
						title="Create board"
						disabled={editing}
						onClick={() => openBoard(false)}
					>
						<Plus size={17} />
					</Button>
				</div>
				<div className={styles.boardOptions}>
					<Checkbox label="Live updates" value={live} changeHandler={setLive} hideFormGroupClass />
					<Checkbox label="Cycle boards" value={cycle} changeHandler={setCycle} hideFormGroupClass />
					<Button
						types={[ButtonTypes.Icon]}
						disabled={editing || !board}
						onClick={() => openBoard(true)}
						aria-label="Board settings"
					>
						<Settings2 size={17} />
						<ChevronDown size={12} />
					</Button>
				</div>
			</nav>
			{boards.isPending ? (
				<div className={styles.state}>
					<Loading loading message="Loading your workspace…" />
				</div>
			) : boards.isError ? (
				<div className={styles.state} role="alert">
					<h2>We couldn’t load your boards</h2>
					<Button onClick={() => boards.refetch()}>Try again</Button>
				</div>
			) : !board ? (
				<div className={styles.state}>
					<LayoutDashboard size={42} />
					<h2>A little space for the big picture</h2>
					<p>Create your first board, then add the reports you check every day.</p>
					<Button types={[ButtonTypes.Primary]} onClick={() => openBoard(false)}>
						<Plus size={17} />
						Create a board
					</Button>
				</div>
			) : widgets.isPending ? (
				<div className={styles.state}>
					<Loading loading message={`Loading ${board.title}…`} />
				</div>
			) : widgets.isError ? (
				<div className={styles.state} role="alert">
					<h2>We couldn’t load this board</h2>
					<Button onClick={() => widgets.refetch()}>Try again</Button>
				</div>
			) : (
				<>
					{catalog.isError && (
						<p className={commonStyles.error}>
							Widget settings are unavailable. <Button onClick={() => catalog.refetch()}>Retry</Button>
						</p>
					)}
					{!widgets.data.length && (
						<div className={styles.state}>
							<LayoutDashboard size={42} />
							<h2>Make room for what matters</h2>
							<p>Add a sales summary, a leaderboard, or a report from the widget library.</p>
							<Button
								types={[ButtonTypes.Primary]}
								disabled={!catalog.data}
								onClick={() => setDialog("addWidget")}
							>
								<Plus size={17} />
								Explore widgets
							</Button>
						</div>
					)}
					<BoardCanvas
						key={board.id}
						widgets={widgets.data}
						boardId={board.id}
						editing={editing}
						setEditing={setEditing}
						refresh={refresh}
						live={live}
						configure={(widget) => {
							if (catalog.data) {
								setActiveWidget(widget);
								setDialog("editWidget");
							}
						}}
						remove={(widget) => {
							setActiveWidget(widget);
							setError("");
							setDialog("deleteWidget");
						}}
					/>
				</>
			)}
			{(dialog === "addWidget" || dialog === "editWidget") && catalog.data && board && (
				<WidgetSettings
					widget={dialog === "editWidget" ? activeWidget : undefined}
					boardId={board.id}
					boards={boards.data ?? []}
					catalog={catalog.data}
					onClose={() => setDialog(undefined)}
					onSaved={async (targetBoardId) => {
						await client.invalidateQueries({ queryKey: ["myViewV2", "widgets"] });
						await client.invalidateQueries({ queryKey: ["myViewV2", "data", activeWidget?.id] });
						setSelected(targetBoardId);
					}}
				/>
			)}
			{dialog && !["addWidget", "editWidget"].includes(dialog) && (
				<Dialog
					title={
						dialog === "newBoard"
							? "Create a board"
							: dialog === "editBoard"
								? "Board settings"
								: dialog === "deleteBoard"
									? "Delete this board?"
									: "Remove this widget?"
					}
					onClose={() => setDialog(undefined)}
					busy={busy}
				>
					<form onSubmit={boardAction}>
						<div className={commonStyles.dialogBody}>
							{dialog === "deleteBoard" || dialog === "deleteWidget" ? (
								<p>
									{dialog === "deleteBoard"
										? `“${board?.title}” and all its widgets will be permanently deleted.`
										: `“${activeWidget?.title}” will be removed from this board.`}
								</p>
							) : (
								<>
									<TextBox
										id="mv-board-name"
										label="Board name"
										autoFocus
										required
										maxlength={100}
										value={boardTitle}
										changeHandler={(value) => setBoardTitle(value)}
										placeholder="e.g. Sales pulse"
									/>
									<div className={styles.cycleOption}>
										<Checkbox
											hideFormGroupClass
											label="Include when cycling boards"
											value={includeInCycle}
											changeHandler={setIncludeInCycle}
										/>
										<p className={commonStyles.muted}>
											Cycling displays each included board for 30 seconds.
										</p>
									</div>
								</>
							)}
							{error && (
								<p className={commonStyles.error} role="alert">
									{error}
								</p>
							)}
						</div>
						<footer className={styles.boardFooter}>
							{dialog === "editBoard" && (
								<Button
									type="button"
									className={styles.boardDelete}
									disabled={busy}
									types={[ButtonTypes.Danger]}
									onClick={() => setDialog("deleteBoard")}
								>
									<Trash2 size={16} />
									Delete board
								</Button>
							)}

							<Button type="button" disabled={busy} onClick={() => setDialog(undefined)}>
								Cancel
							</Button>
							<Button
								disabled={busy}
								types={[dialog.startsWith("delete") ? ButtonTypes.Danger : ButtonTypes.Primary]}
								type="submit"
							>
								{busy ? "Saving…" : dialog.startsWith("delete") ? "Delete" : "Save board"}
							</Button>
						</footer>
					</form>
				</Dialog>
			)}
		</main>
	);
}
